import { z } from "zod";

const minorAmount = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const currency = z.enum(["USD", "EUR", "GBP", "JPY", "AUD", "CAD"]);
export const PaymentStatusSchema = z.enum([
  "preparing", "requires_payment_method", "requires_confirmation", "requires_action",
  "processing", "succeeded", "canceled",   "canceling", "reconciliation_required", "voided",
]);
  export const PaymentVoidReasonSchema = z.enum(["not_created", "owner_override"]);
const PaymentDtoSchema = z.object({
  id: z.string().uuid(), amount_minor: minorAmount.positive(), currency,
  status: PaymentStatusSchema, failure_message: z.string().nullable(),
  refunded_amount_minor: minorAmount, dispute_status: z.string().nullable(),
  card: z.object({ brand: z.string(), last4: z.string().regex(/^\d{4}$/) }).nullable(),
  void_reason: PaymentVoidReasonSchema.nullable(), cancel_requested: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
});
const PaymentSchema = z.object({
  id: z.string().uuid(), amountMinor: minorAmount.positive(), currency,
  status: PaymentStatusSchema, failureMessage: z.string().nullable(),
  refundedAmountMinor: minorAmount, disputeStatus: z.string().nullable(),
  card: z.object({ brand: z.string(), last4: z.string().regex(/^\d{4}$/) }).nullable(),
  voidReason: PaymentVoidReasonSchema.nullable(), cancelRequested: z.boolean(),
  createdAt: z.date(),
});
const CashPaymentDtoSchema = z.object({
  id: z.string().uuid(), amount_minor: minorAmount.positive(), currency,
  created_at: z.string().datetime({ offset: true }), voided_at: z.string().datetime({ offset: true }).nullable(),
});
const CashPaymentSchema = z.object({
  id: z.string().uuid(), amountMinor: minorAmount.positive(), currency,
  createdAt: z.date(), voidedAt: z.date().nullable(),
});
const PaymentSummaryDtoSchema = z.object({
  environment: z.enum(["test", "live"]), currency: z.string(),
  total_minor: minorAmount.nullable(), collected_minor: minorAmount,
  card_collected_minor: minorAmount, cash_collected_minor: minorAmount,
  balance_minor: minorAmount.nullable(), billing_locked: z.boolean(),
  can_charge: z.boolean(), unavailable_reason: z.string().nullable(),
  can_record_cash: z.boolean(), cash_unavailable_reason: z.string().nullable(),
  active_payment: PaymentDtoSchema.nullable(), payments: z.array(PaymentDtoSchema),
  cash_payments: z.array(CashPaymentDtoSchema),
});
const PaymentSessionDtoSchema = z.object({
  id: z.string().uuid(), token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  expires_at: z.string().datetime({ offset: true }), url: z.string().url(),
});
const PaymentDetailsDtoSchema = z.object({
  business_name: z.string(), customer_name: z.string(), environment: z.enum(["test", "live"]),
  amount_minor: minorAmount.positive(), currency,
  expires_at: z.string().datetime({ offset: true }),
  publishable_key: z.string().regex(/^pk_(test|live)_/),
  stripe_account_id: z.string().regex(/^acct_[A-Za-z0-9]+$/), payment: PaymentDtoSchema,
});
const PaymentPreparationDtoSchema = z.object({
  client_secret: z.string().regex(/^pi_[A-Za-z0-9]+_secret_[A-Za-z0-9]+$/),
  publishable_key: z.string().regex(/^pk_(test|live)_/),
  stripe_account_id: z.string().regex(/^acct_[A-Za-z0-9]+$/),
  mode: z.enum(["pay", "authenticate"]), payment_method_id: z.string().regex(/^pm_[A-Za-z0-9]+$/).optional(),
});
export const CreatePaymentDtoSchema = z.object({
  amount_minor: minorAmount.positive(), idempotency_key: z.string().uuid(),
  expected_revision: z.number().int().positive(),
  expected_currency: currency,
  method: z.enum(["new_card", "saved_card"]),
  payment_method_id: z.string().optional(),
  acknowledgement_version: z.literal("appointment-charge-v1").optional(),
  acknowledged: z.literal(true).optional(),
});
export const RecordCashPaymentDtoSchema = z.object({
  amount_minor: minorAmount.positive(), idempotency_key: z.string().uuid(), expected_currency: currency,
});
export const VoidPaymentDtoSchema = z.object({
  idempotency_key: z.string().uuid(), acknowledged: z.literal(true),
  acknowledgement_version: z.literal("appointment-void-v1"),
});
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;
export type PaymentDto = z.infer<typeof PaymentDtoSchema>;
export type Payment = z.infer<typeof PaymentSchema>;
export type PaymentSummaryDto = z.infer<typeof PaymentSummaryDtoSchema>;
export type PaymentSessionDto = z.infer<typeof PaymentSessionDtoSchema>;
export type PaymentDetailsDto = z.infer<typeof PaymentDetailsDtoSchema>;
export type PaymentPreparationDto = z.infer<typeof PaymentPreparationDtoSchema>;
export type CreatePaymentDto = z.infer<typeof CreatePaymentDtoSchema>;
export type VoidPaymentDto = z.infer<typeof VoidPaymentDtoSchema>;
export type CashPaymentDto = z.infer<typeof CashPaymentDtoSchema>;
export type CashPayment = z.infer<typeof CashPaymentSchema>;
export type RecordCashPaymentDto = z.infer<typeof RecordCashPaymentDtoSchema>;
export interface RecordCashPaymentRequest { amountMinor: number; idempotencyKey: string; expectedCurrency: string }
export const RecordCashPaymentRequestSchema = z.object({
  amountMinor: minorAmount.positive(), idempotencyKey: z.uuid(), expectedCurrency: currency,
});
export type PaymentVoidReason = z.infer<typeof PaymentVoidReasonSchema>;
export interface CreatePaymentRequest {
  amountMinor: number; idempotencyKey: string; expectedRevision: number; expectedCurrency: string;
  method: "new_card" | "saved_card"; paymentMethodId?: string; acknowledged?: true;
}
export function fromPaymentDtoToPayment(input: PaymentDto): Payment {
  const dto = PaymentDtoSchema.parse(input);
  return PaymentSchema.parse({
    id: dto.id, amountMinor: dto.amount_minor, currency: dto.currency, status: dto.status,
    failureMessage: dto.failure_message, refundedAmountMinor: dto.refunded_amount_minor,
    disputeStatus: dto.dispute_status, card: dto.card, voidReason: dto.void_reason,
    cancelRequested: dto.cancel_requested, createdAt: new Date(dto.created_at),
  });
}
export function fromCashPaymentDtoToCashPayment(input: CashPaymentDto): CashPayment {
  const dto = CashPaymentDtoSchema.parse(input);
  return CashPaymentSchema.parse({
    id: dto.id, amountMinor: dto.amount_minor, currency: dto.currency,
    createdAt: new Date(dto.created_at), voidedAt: dto.voided_at ? new Date(dto.voided_at) : null,
  });
}
export function fromRecordCashPaymentRequestToRecordCashPaymentDto(request: RecordCashPaymentRequest): RecordCashPaymentDto {
  return RecordCashPaymentDtoSchema.parse({
    amount_minor: request.amountMinor, idempotency_key: request.idempotencyKey, expected_currency: request.expectedCurrency,
  });
}
export function fromPaymentSummaryDtoToPaymentSummary(input: PaymentSummaryDto) {
  const dto = PaymentSummaryDtoSchema.parse(input);
  return {
    environment: dto.environment, currency: dto.currency, totalMinor: dto.total_minor,
    collectedMinor: dto.collected_minor, cardCollectedMinor: dto.card_collected_minor,
    cashCollectedMinor: dto.cash_collected_minor, balanceMinor: dto.balance_minor,
    billingLocked: dto.billing_locked, canCharge: dto.can_charge,
    unavailableReason: dto.unavailable_reason, canRecordCash: dto.can_record_cash,
    cashUnavailableReason: dto.cash_unavailable_reason,
    activePayment: dto.active_payment ? fromPaymentDtoToPayment(dto.active_payment) : null,
    payments: dto.payments.map(fromPaymentDtoToPayment),
    cashPayments: dto.cash_payments.map(fromCashPaymentDtoToCashPayment),
  };
}
export function fromPaymentSessionDtoToPaymentSession(input: PaymentSessionDto) {
  const dto = PaymentSessionDtoSchema.parse(input);
  const url = new URL(dto.url);
  if (url.origin !== window.location.origin || url.username || url.password || url.pathname !== "/payments/pay" || url.search ||
    url.hash !== `#token=${dto.token}`) throw new Error("The secure payment link was invalid.");
  return { id: dto.id, token: dto.token, expiresAt: new Date(dto.expires_at), url: url.toString() };
}
export function fromPaymentDetailsDtoToPaymentDetails(input: PaymentDetailsDto) {
  const dto = PaymentDetailsDtoSchema.parse(input);
  if (dto.payment.amount_minor !== dto.amount_minor || dto.payment.currency !== dto.currency ||
    !dto.publishable_key.startsWith(`pk_${dto.environment}_`)) throw new Error("Payment details did not match.");
  return {
    businessName: dto.business_name, customerName: dto.customer_name, environment: dto.environment,
    amountMinor: dto.amount_minor, currency: dto.currency, expiresAt: new Date(dto.expires_at),
    publishableKey: dto.publishable_key, stripeAccountId: dto.stripe_account_id,
    payment: fromPaymentDtoToPayment(dto.payment),
  };
}
export function fromPaymentPreparationDtoToPaymentPreparation(input: PaymentPreparationDto) {
  const dto = PaymentPreparationDtoSchema.parse(input);
  return {
    clientSecret: dto.client_secret, publishableKey: dto.publishable_key,
    stripeAccountId: dto.stripe_account_id, mode: dto.mode, paymentMethodId: dto.payment_method_id,
  };
}
export function fromCreatePaymentRequestToCreatePaymentDto(request: CreatePaymentRequest): CreatePaymentDto {
  return CreatePaymentDtoSchema.parse({
    amount_minor: request.amountMinor, idempotency_key: request.idempotencyKey,
    expected_revision: request.expectedRevision, expected_currency: request.expectedCurrency, method: request.method,
    ...(request.method === "saved_card" ? {
      payment_method_id: request.paymentMethodId,
      acknowledged: request.acknowledged, acknowledgement_version: "appointment-charge-v1",
    } : {}),
  });
}
export function fromVoidPaymentKeyToVoidPaymentDto(idempotencyKey: string): VoidPaymentDto {
  return VoidPaymentDtoSchema.parse({ idempotency_key: idempotencyKey, acknowledged: true, acknowledgement_version: "appointment-void-v1" });
}
export type PaymentSummary = ReturnType<typeof fromPaymentSummaryDtoToPaymentSummary>;
export type PaymentSession = ReturnType<typeof fromPaymentSessionDtoToPaymentSession>;
export type PaymentDetails = ReturnType<typeof fromPaymentDetailsDtoToPaymentDetails>;
export type PaymentPreparation = ReturnType<typeof fromPaymentPreparationDtoToPaymentPreparation>;

export const paymentStatusLabels: Record<PaymentStatus, string> = {
  preparing: "Preparing payment", requires_payment_method: "Awaiting customer payment",
  requires_confirmation: "Awaiting confirmation", requires_action: "Customer verification needed",
  processing: "Processing", succeeded: "Paid", canceled: "Canceled",
  canceling: "Canceling payment", reconciliation_required: "Payment outcome needs review", voided: "Voided",
};
export function isPaymentTerminal(payment: Payment) {
  return payment.status === "succeeded" || payment.status === "canceled" || payment.status === "voided";
}
export function paymentCurrencyDigits(value: string): number | null {
  return currency.safeParse(value).success ? value === "JPY" ? 0 : 2 : null;
}
export function parsePaymentAmount(value: string, currencyCode: string): number | null {
  const digits = paymentCurrencyDigits(currencyCode);
  if (digits === null || !(digits === 0 ? /^\d+$/ : /^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/).test(value)) return null;
  const [whole, fraction = ""] = value.split(".");
  const amount = Number(`${whole || "0"}${fraction.padEnd(digits, "0")}`);
  return Number.isSafeInteger(amount) && amount > 0 ? amount : null;
}
export function paymentAmountInput(amount: number, currencyCode: string) {
  const digits = paymentCurrencyDigits(currencyCode);
  if (digits === null || !Number.isSafeInteger(amount) || amount < 0) throw new Error("Unsupported payment amount.");
  const value = String(amount).padStart(digits + 1, "0");
  return digits ? `${value.slice(0, -digits)}.${value.slice(-digits)}` : value;
}
export function formatPaymentMoney(amount: number, currencyCode: string) {
  const digits = paymentCurrencyDigits(currencyCode);
  if (digits === null) return `${amount} minor units (${currencyCode})`;
  return new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode }).format(amount / 10 ** digits);
}
